import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Button } from '../../components/ui/button';
import { Plus, Building, Search, Pencil, Trash2, UsersRound } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { VenueDialog } from '../../components/user/VenueDialog';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const Venues = () => {
  const [venues, setVenues] = useState([]);
  const [sections, setSections] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [user, setUser] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchSections(parsedUser.entityId);
      fetchVenues(parsedUser.entityId);
    }
  }, []);

  const fetchSections = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      if (response.data.success) {
        setSections(response.data.sections);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchVenues = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/venues/${propertyId}`);
      if (response.data.success) {
        setVenues(response.data.venues);
        setFilteredVenues(response.data.venues);
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
      toast({
        title: "Error",
        description: "Failed to fetch venues",
        variant: "destructive"
      });
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setFilteredVenues(venues);
    } else {
      const filtered = venues.filter(venue => 
        venue.name.toLowerCase().includes(term)
      );
      setFilteredVenues(filtered);
    }
  };

  const handleSave = async (data) => {
    try {
      let response;
      
      if (selectedVenue) {
        // Update existing
        response = await axios.put(
          `${BACKEND_URL}/api/venues/${selectedVenue.id}`,
          { name: data.name, sectionIds: data.sectionIds }
        );
      } else {
        // Create new
        response = await axios.post(
          `${BACKEND_URL}/api/venues`,
          {
            propertyId: user.entityId,
            name: data.name,
            sectionIds: data.sectionIds
          }
        );
      }
      
      console.log('Venue save response:', response.data);
      
      // Close dialog and refresh
      setIsDialogOpen(false);
      setSelectedVenue(null);
      
      // Show success message
      toast({
        title: "Success",
        description: selectedVenue ? "Venue updated successfully" : "Venue created successfully"
      });
      
      // Refresh venues list
      await fetchVenues(user.entityId);
      
    } catch (error) {
      console.error('Error saving venue:', error);
      console.error('Error response:', error.response);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save venue",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (venueId) => {
    if (!window.confirm('Are you sure you want to delete this venue?')) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/venues/${venueId}`);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Venue deleted successfully"
        });
        fetchVenues(user.entityId);
      }
    } catch (error) {
      console.error('Error deleting venue:', error);
      toast({
        title: "Error",
        description: "Failed to delete venue",
        variant: "destructive"
      });
    }
  };

  const getSectionsForVenue = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    if (!venue || !venue.sectionIds) return [];
    return sections.filter(s => venue.sectionIds.includes(s.id));
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Venues</h1>
            <p className="text-slate-600 mt-1">Manage your venues and their sections</p>
          </div>
          <Button 
            onClick={() => {
              setSelectedVenue(null);
              setIsDialogOpen(true);
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Venue
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search venues..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        {/* Venues Grid */}
        {filteredVenues.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-slate-200">
            <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No venues found</h3>
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'Try a different search term' : 'Get started by creating your first venue'}
            </p>
            {!searchTerm && (
              <Button 
                onClick={() => {
                  setSelectedVenue(null);
                  setIsDialogOpen(true);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Venue
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVenues.map((venue) => {
              const venueSections = getSectionsForVenue(venue.id);
              
              return (
                <div 
                  key={venue.id} 
                  className="bg-white rounded-lg shadow-md border border-slate-200 hover:shadow-lg transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Building className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-800">{venue.name}</h3>
                          <p className="text-sm text-slate-500">
                            {venueSections.length} {venueSections.length === 1 ? 'section' : 'sections'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedVenue(venue);
                            setIsDialogOpen(true);
                          }}
                          className="text-slate-600 hover:text-teal-600"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(venue.id)}
                          className="text-slate-600 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Sections */}
                    {venueSections.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Sections:</p>
                        <div className="flex flex-wrap gap-2">
                          {venueSections.map((section) => (
                            <span 
                              key={section.id} 
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-700"
                            >
                              <UsersRound className="w-3 h-3 mr-1" />
                              {section.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">No sections assigned</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Venue Dialog */}
      <VenueDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setSelectedVenue(null);
        }}
        venue={selectedVenue}
        sections={sections}
        onSave={handleSave}
      />
    </UserLayout>
  );
};
